import Icon from '@/components/ui/AppIcon';

interface HealthMetric {
  id: number;
  name: string;
  status: 'healthy' | 'warning' | 'critical';
  value: string;
  lastChecked: string;
  description: string;
}

export default function SystemHealth() {
  const metrics: HealthMetric[] = [
    {
      id: 1,
      name: "Server Performance",
      status: "healthy",
      value: "98.5%",
      lastChecked: "2 minutes ago",
      description: "CPU and memory utilization within normal range"
    },
    {
      id: 2,
      name: "Network Connectivity",
      status: "healthy",
      value: "100%",
      lastChecked: "1 minute ago",
      description: "All network connections stable and responsive"
    },
    {
      id: 3,
      name: "Backup Status",
      status: "healthy",
      value: "Success",
      lastChecked: "4 hours ago",
      description: "Last backup completed successfully at 2:00 AM"
    },
    {
      id: 4,
      name: "Security Scans",
      status: "warning",
      value: "2 Updates",
      lastChecked: "12 hours ago",
      description: "Security patches available for installation"
    }
  ];

  const getStatusIcon = (status: HealthMetric['status']) => {
    switch (status) {
      case 'healthy':
        return { name: 'CheckCircleIcon' as const, color: 'text-success' };
      case 'warning':
        return { name: 'ExclamationTriangleIcon' as const, color: 'text-warning' };
      case 'critical':
        return { name: 'XCircleIcon' as const, color: 'text-error' };
      default:
        return { name: 'QuestionMarkCircleIcon' as const, color: 'text-muted-foreground' };
    }
  };

  const getStatusColor = (status: HealthMetric['status']) => {
    switch (status) {
      case 'healthy':
        return 'bg-success/10 border-success/20';
      case 'warning':
        return 'bg-warning/10 border-warning/20';
      case 'critical':
        return 'bg-error/10 border-error/20';
      default:
        return 'bg-muted border-border';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="px-6 py-4 border-b border-border bg-muted/30 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-headline font-bold text-primary">System Health</h2>
          <p className="text-sm text-muted-foreground mt-1">Real-time monitoring of your IT infrastructure</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-success/10 rounded-lg border border-success/20">
          <div className="w-2 h-2 bg-success rounded-full animate-pulse-slow" />
          <span className="text-xs font-cta font-semibold text-success">All Systems Operational</span>
        </div>
      </div>
      
      <div className="divide-y divide-border">
        {metrics.map((metric) => {
          const statusIcon = getStatusIcon(metric.status);
          return (
            <div key={metric.id} className="p-6 hover:bg-muted/30 transition-colors duration-200">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-lg border flex items-center justify-center flex-shrink-0 ${getStatusColor(metric.status)}`}>
                  <Icon 
                    name={statusIcon.name} 
                    size={24} 
                    variant="solid"
                    className={statusIcon.color}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-base font-headline font-semibold text-primary">
                      {metric.name}
                    </h3>
                    <span className="text-lg font-headline font-bold text-primary">
                      {metric.value}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {metric.description}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="ClockIcon" size={12} variant="outline" />
                    <span>Last checked: {metric.lastChecked}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="px-6 py-4 border-t border-border bg-muted/30">
        <button className="text-sm font-cta font-semibold text-brand-primary hover:text-brand-primary/80 transition-colors duration-200 flex items-center gap-2">
          <span>View Detailed Monitoring</span>
          <Icon name="ArrowRightIcon" size={16} variant="outline" />
        </button>
      </div>
    </div>
  );
}